// Helpers for the custom participant restriction rules (application and website blacklist).
// Custom rules are kept even if they overlap a preset, so a preset match is only a hint.

export function normalizeRuleValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");
}

// Website presets store their url as a regex, e.g. ^(?:https?:\/\/)?(?:.*\.)?comdirect\.de(?:[/?#:]|$)
// Shown as the plain domain, wildcards as *, e.g. comdirect.de or sparkasse-*.de
export function presetUrlToDomain(url) {
  return String(url || "")
    .replace(/^\^/, "")
    .replace("(?:https?:\\/\\/)?", "")
    .replace("(?:.*\\.)?", "")
    .replace(/\(\?:\[[^\]]*\]\|\$\)$/, "")
    .replace(/\$$/, "")
    .replace(/\\(.)/g, "$1")
    .replaceAll(".*", "*");
}

// The recorder reads window titles and website urls as regular expressions and searches them anywhere.
// Entries are saved as patterns, * stays a wildcard, everything else is matched literally.
const WEBSITE_PATTERN_START = "^(?:https?:\\/\\/)?(?:.*\\.)?";
const WEBSITE_PATTERN_END = "(?:[/?#:]|$)";

function escapeRegex(text) {
  return String(text).replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
}

function wildcardPattern(text) {
  return String(text).split("*").map(escapeRegex).join(".*");
}

export function isWebsitePattern(value) {
  return String(value || "").startsWith(WEBSITE_PATTERN_START);
}

// amazon.com becomes the preset form, amazon.com/music matches that path on the domain
export function websitePattern(value) {
  if (!value || isWebsitePattern(value)) return value || "";
  const [domain, ...path] = normalizeRuleValue(value).split("/");
  return path.length
    ? `${WEBSITE_PATTERN_START}${wildcardPattern(domain)}\\/${wildcardPattern(path.join("/"))}`
    : `${WEBSITE_PATTERN_START}${wildcardPattern(domain)}${WEBSITE_PATTERN_END}`;
}

export function windowTitlePattern(title) {
  return title ? wildcardPattern(title) : "";
}

// Shown and edited as plain text again, * for wildcards
export function windowTitleText(pattern) {
  return String(pattern || "")
    .split(".*")
    .map((part) => part.replace(/\\(.)/g, "$1"))
    .join("*");
}

// The recorder compares process names without the extension (mspaint, not mspaint.exe).
export function appProcessName(appname) {
  return String(appname || "")
    .trim()
    .replace(/\.exe$/i, "");
}

export function appDisplayName(appname) {
  const base = appProcessName(appname);
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export function websiteDisplayName(url) {
  return normalizeRuleValue(url).split("/")[0];
}

// A website needs at least a dot and a letter ending, e.g. amazon.com or mail.google.com.
// Protocol, www. and the path are ignored.
const WEBSITE_DOMAIN = /^([a-z0-9-]+\.)+[a-z]{2,}$/;

export function isValidWebsite(url) {
  return WEBSITE_DOMAIN.test(websiteDisplayName(url));
}

// A sub-URL only takes effect on the website it belongs to.
export function isSubUrlOfWebsite(suburl, url) {
  const normalizedSubUrl = normalizeRuleValue(suburl);
  return (
    !normalizedSubUrl ||
    websiteDisplayName(normalizedSubUrl) === websiteDisplayName(url)
  );
}

export function appMatches(rule, value) {
  return (
    !!normalizeRuleValue(appProcessName(value)) &&
    normalizeRuleValue(appProcessName(rule.appname)) === normalizeRuleValue(appProcessName(value))
  );
}

export function websiteMatches(rule, value) {
  const normalizedValue = normalizeRuleValue(value);
  if (!normalizedValue) return false;
  if (normalizeRuleValue(rule.url) === normalizedValue) return true;
  try {
    return new RegExp(rule.url, "i").test(normalizedValue);
  } catch {
    return false;
  }
}

export function findPresetMatch(groups, value, matches) {
  for (const group of groups || []) {
    const preset = (group.preset || []).find((rule) => matches(rule, value));
    if (preset) return { group, preset };
  }
  return null;
}

export function findCustomMatch(groups, value, matches, skip = {}) {
  return (groups || []).some((group, listId) =>
    (group.user || []).some(
      (rule, ruleId) =>
        !(listId === skip.listId && ruleId === skip.ruleId) &&
        matches(rule, value),
    ),
  );
}

// Preset suggestions for the rule input, matched by preset name, value or list name.
export function findPresetSuggestions(groups, term, toValue, limit = 6) {
  const normalizedTerm = normalizeRuleValue(term);
  if (normalizedTerm.length < 2) return [];

  const suggestions = [];
  for (const group of groups || []) {
    for (const preset of group.preset || []) {
      const value = toValue(preset);
      if (normalizeRuleValue(value) === normalizedTerm) return [];
      if (
        [preset.name, value, group.name].some((text) =>
          normalizeRuleValue(text).includes(normalizedTerm),
        )
      )
        suggestions.push({ group: group.name, preset, value });
    }
  }
  return suggestions.slice(0, limit);
}
